import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import * as api from '../../services/api';

// ─── Timezone + filename helpers ─────────────────────────────────────────────

const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function parseActivityFilename(filename: string): { date: Date; label: string } | null {
  const m = filename.match(/activity-(\d+)/);
  if (!m) return null;
  const d = new Date(parseInt(m[1]) * 1000);
  return {
    date: d,
    label: d.toLocaleDateString('en-US', {
      timeZone: USER_TZ,
      month: 'short', day: 'numeric', year: 'numeric',
    }),
  };
}

function toLocalYMD(date: Date): string {
  return date.toLocaleDateString('en-CA', { timeZone: USER_TZ }); // YYYY-MM-DD
}

function ymdToYearMonth(ymd: string): { year: number; month: number } {
  const [y, m] = ymd.split('-').map(Number);
  return { year: y, month: m - 1 };
}

function fmtYMD(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function today(): string {
  return toLocalYMD(new Date());
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────

function calDays(year: number, month: number): (string | null)[] {
  const first = new Date(year, month, 1).getDay(); // 0=Sun
  const total = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array(first).fill(null);
  for (let d = 1; d <= total; d++) {
    cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return cells;
}

function inRange(date: string, start: string | null, end: string | null): boolean {
  if (!start || !end) return false;
  const [s, e] = start <= end ? [start, end] : [end, start];
  return date >= s && date <= e;
}

// ─── Overlay + modal ─────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(10, 18, 40, 0.72);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  animation: ${fadeIn} 0.18s ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 18px;
  width: 92vw;
  max-width: 980px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0,0,0,0.24);
  animation: ${slideUp} 0.22s ease;
`;

const ModalHead = styled.div`
  padding: 22px 28px 18px;
  border-bottom: 1px solid #E2EAF4;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 18px; font-weight: 800; color: #0F2042; margin: 0;
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px;
  border: none; background: #F1F5F9; cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center; color: #64748B;
  &:hover { background: #E2EAF4; }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 0;
  flex: 1;
  overflow: hidden;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const LeftPane = styled.div`
  padding: 24px 22px;
  border-right: 1px solid #E2EAF4;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const RightPane = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightTop = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #E2EAF4;
  flex-shrink: 0;
`;

const RightBottom = styled.div`
  padding: 16px 24px 20px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFoot = styled.div`
  padding: 14px 28px;
  border-top: 1px solid #E2EAF4;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
`;

// ─── Form elements ────────────────────────────────────────────────────────────

const Label = styled.div`
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748B; margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%; padding: 9px 12px; font-size: 13.5px; border-radius: 8px;
  border: 1.5px solid #E2EAF4; color: #0F2042; outline: none; box-sizing: border-box;
  &:focus { border-color: #0369A1; }
`;

const Textarea = styled.textarea`
  width: 100%; padding: 10px 12px; font-size: 13px; border-radius: 8px;
  border: 1.5px solid #E2EAF4; color: #0F2042; outline: none; resize: vertical;
  min-height: 130px; line-height: 1.6; font-family: inherit; box-sizing: border-box;
  &:focus { border-color: #0369A1; }
`;

const DateInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 14px;
`;

const DateInputWrap = styled.div``;

const DateLabel = styled.div`
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
  color: #94A3B8; margin-bottom: 4px;
`;

const DateInput = styled.input`
  width: 100%; padding: 7px 10px; font-size: 12.5px; border-radius: 8px;
  border: 1.5px solid #E2EAF4; color: #0F2042; outline: none; box-sizing: border-box;
  &:focus { border-color: #0369A1; }
`;

const TodayBtn = styled.button`
  padding: 7px 11px; font-size: 11.5px; font-weight: 700; border-radius: 8px;
  border: 1.5px solid #CBD5E1; background: #fff; color: #374151; cursor: pointer; white-space: nowrap;
  &:hover { border-color: #0369A1; color: #0369A1; }
`;

const BtnPrimary = styled.button<{ $disabled?: boolean }>`
  padding: 9px 20px; font-size: 13.5px; font-weight: 700; border-radius: 9px;
  border: none; background: linear-gradient(135deg, #0F2042, #0369A1);
  color: #fff; cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.$disabled ? 0.55 : 1};
  &:hover:not(:disabled) { opacity: 0.9; }
`;

const BtnSecondary = styled.button`
  padding: 9px 18px; font-size: 13.5px; font-weight: 600; border-radius: 9px;
  border: 1.5px solid #E2EAF4; background: #fff; color: #374151; cursor: pointer;
  &:hover { background: #F8FAFC; }
`;

// ─── Calendar ─────────────────────────────────────────────────────────────────

const CalNav = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
`;

const CalNavBtn = styled.button`
  width: 28px; height: 28px; border: none; background: #F1F5F9; border-radius: 6px;
  cursor: pointer; font-size: 13px; color: #374151;
  &:hover { background: #E2EAF4; }
`;

const CalMonth = styled.div`
  font-size: 14px; font-weight: 700; color: #0F2042;
`;

const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  user-select: none;
`;

const CalDOW = styled.div`
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: #94A3B8; text-align: center; padding: 4px 0;
`;

const CalDay = styled.div<{
  $today?: boolean; $inRange?: boolean; $isEdge?: boolean;
  $hasSession?: boolean; $empty?: boolean;
}>`
  height: 32px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border-radius: 6px;
  font-size: 12px; font-weight: ${p => p.$today ? 700 : 400};
  cursor: ${p => p.$empty ? 'default' : 'pointer'};
  background: ${p => p.$isEdge ? '#0369A1' : p.$inRange ? '#DBEAFE' : 'transparent'};
  color: ${p => p.$isEdge ? '#fff' : p.$today ? '#0369A1' : '#374151'};
  border: ${p => p.$today && !p.$inRange && !p.$isEdge ? '1.5px solid #0369A1' : '1.5px solid transparent'};
  transition: background 0.1s;
  position: relative;
  &:hover { background: ${p => p.$empty ? 'transparent' : p.$isEdge ? '#0369A1' : '#EFF6FF'}; }
`;

const SessionDot = styled.div`
  width: 5px; height: 5px; border-radius: 50%;
  background: #F59E0B; position: absolute; bottom: 3px;
`;

// ─── Session assignment ───────────────────────────────────────────────────────

const AssignRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

const DropZone = styled.div<{ $over?: boolean }>`
  border: 2px dashed ${p => p.$over ? '#0369A1' : '#CBD5E1'};
  border-radius: 12px; padding: 12px;
  background: ${p => p.$over ? '#EFF6FF' : '#FAFBFC'};
  min-height: 80px;
  transition: border-color 0.12s, background 0.12s;
`;

const DropZoneLabel = styled.div<{ $color: string }>`
  font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: ${p => p.$color}; margin-bottom: 8px;
`;

const DropHint = styled.div`
  font-size: 11.5px; color: #94A3B8; text-align: center; padding: 8px 0;
`;

const SessionChip = styled.div<{ $color?: string }>`
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1.5px solid #E2EAF4; border-radius: 8px;
  padding: 7px 10px; cursor: grab; margin-bottom: 6px;
  font-size: 12px; color: #0F2042;
  &:hover { border-color: #0369A1; }
  &:active { cursor: grabbing; }
`;

const ChipDot = styled.div<{ $color: string }>`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.$color}; flex-shrink: 0;
`;

const UnassignedRow = styled.div`
  margin-top: 14px;
`;

const UploadInZone = styled.label`
  display: flex; align-items: center; gap: 6px;
  font-size: 11.5px; color: #0369A1; cursor: pointer; margin-top: 8px;
  &:hover { text-decoration: underline; }
`;

// ─── Exercise multi-select ────────────────────────────────────────────────────

const ExerciseChipRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;
`;

const ExerciseChip = styled.div`
  display: inline-flex; align-items: center; gap: 5px;
  background: #EFF6FF; border: 1.5px solid #BFDBFE; border-radius: 20px;
  padding: 3px 10px 3px 10px; font-size: 12px; color: #1D4ED8; font-weight: 500;
`;

const ExerciseChipX = styled.span`
  cursor: pointer; color: #93C5FD; font-size: 14px; line-height: 1;
  &:hover { color: #1D4ED8; }
`;

const ExerciseDropdown = styled.div`
  position: absolute; z-index: 200; left: 0; right: 0; top: calc(100% + 2px);
  background: #fff; border: 1.5px solid #E2EAF4; border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.10); max-height: 200px; overflow-y: auto;
`;

const ExerciseOption = styled.div<{ $selected?: boolean }>`
  padding: 9px 14px; cursor: pointer; font-size: 13px;
  background: ${p => p.$selected ? '#EFF6FF' : '#fff'};
  display: flex; align-items: center; gap: 8px;
  &:hover { background: #F0F9FF; }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface SkSession {
  id: string;
  session_date: string;
  source_filename: string;
  shot_count: number;
}

interface PatientOption {
  patient_uuid: string;
  first_name: string;
  last_name: string;
  django_user_id?: number;
}

interface Props {
  sessions: SkSession[];
  onClose: () => void;
  onCreated: () => void;
  isPatientSession?: boolean;
}

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

// ─── Component ────────────────────────────────────────────────────────────────

const CreateTrainingModal: React.FC<Props> = ({ sessions, onClose, onCreated, isPatientSession = false }) => {
  const todayStr = today();
  const todayDate = new Date();

  const [title, setTitle] = useState('');
  const [exercises, setExercises] = useState('');
  const [calYear, setCalYear] = useState(todayDate.getFullYear());
  const [calMonth, setCalMonth] = useState(todayDate.getMonth());
  const [startDate, setStartDate] = useState<string | null>(todayStr);
  const [endDate, setEndDate] = useState<string | null>(todayStr);
  const [isDraggingCal, setIsDraggingCal] = useState(false);
  const [calDragStart, setCalDragStart] = useState<string | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [preIds, setPreIds] = useState<string[]>([]);
  const [postIds, setPostIds] = useState<string[]>([]);
  const [dropOver, setDropOver] = useState<'pre' | 'post' | null>(null);
  const [saving, setSaving] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [uploadingPre, setUploadingPre] = useState(false);
  const [uploadingPost, setUploadingPost] = useState(false);
  const [allSessions, setAllSessions] = useState<SkSession[]>(sessions);
  const [sessionsLoading, setSessionsLoading] = useState(sessions.length === 0);
  const preFileRef = useRef<HTMLInputElement>(null);
  const postFileRef = useRef<HTMLInputElement>(null);

  // Patient selector (clinician only)
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<PatientOption[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientSearchRef = useRef<HTMLInputElement>(null);

  // Homework exercise selector
  const [hwAssignments, setHwAssignments] = useState<api.HomeworkAssignment[]>([]);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState<string[]>([]);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [showExerciseDropdown, setShowExerciseDropdown] = useState(false);

  // Fetch patients for clinician dropdown
  useEffect(() => {
    if (isPatientSession) return;
    setPatientsLoading(true);
    api.getPortalPatients().then((res: any) => {
      if (res?.patients) {
        setPatientOptions(res.patients);
      }
    }).catch(() => {}).finally(() => setPatientsLoading(false));
  }, [isPatientSession]);

  // Fetch homework assignments for exercise selector
  useEffect(() => {
    if (isPatientSession) {
      api.getMyHomework().then((assignments: api.HomeworkAssignment[]) => {
        setHwAssignments(assignments.filter(a => a.is_active));
      }).catch(() => {});
    }
  }, [isPatientSession]);

  useEffect(() => {
    if (isPatientSession || !selectedPatient?.django_user_id) return;
    api.getPatientHomeworkAssignments(selectedPatient.django_user_id).then((assignments: api.HomeworkAssignment[]) => {
      setHwAssignments(assignments.filter(a => a.is_active));
      setSelectedExerciseIds([]);
    }).catch(() => {});
  }, [isPatientSession, selectedPatient]);

  const filteredPatients = patientSearch
    ? patientOptions.filter(p => {
        const full = `${p.first_name} ${p.last_name}`.toLowerCase();
        const uuid = p.patient_uuid.toLowerCase();
        const q = patientSearch.toLowerCase();
        return full.includes(q) || uuid.startsWith(q);
      })
    : patientOptions;

  // When patient changes, reload their SkyTrak sessions
  useEffect(() => {
    if (isPatientSession) {
      (api as any).getSkyTrakSessions().then((res: any) => {
        if (res?.success) setAllSessions(res.sessions ?? []);
      }).catch(() => {}).finally(() => setSessionsLoading(false));
      return;
    }
    if (!selectedPatient) { setAllSessions([]); setSessionsLoading(false); return; }
    setSessionsLoading(true);
    setPreIds([]); setPostIds([]);
    (api as any).getSkyTrakSessions(selectedPatient.patient_uuid).then((res: any) => {
      if (res?.success) setAllSessions(res.sessions ?? []);
    }).catch(() => {}).finally(() => setSessionsLoading(false));
  }, [isPatientSession, selectedPatient]);

  // Calendar range during mouse drag
  const tempStart = isDraggingCal && calDragStart && hoverDate
    ? [calDragStart, hoverDate].sort()[0] : startDate;
  const tempEnd = isDraggingCal && calDragStart && hoverDate
    ? [calDragStart, hoverDate].sort()[1] : endDate;

  // Sessions grouped by date for calendar dots
  const sessionsByDate = allSessions.reduce<Record<string, SkSession[]>>((acc, s) => {
    acc[s.session_date] = [...(acc[s.session_date] || []), s];
    return acc;
  }, {});

  // Categorize sessions — unassigned filtered to selected date range
  const unassigned = allSessions.filter(s =>
    !preIds.includes(s.id) &&
    !postIds.includes(s.id) &&
    (!startDate || !endDate || inRange(s.session_date, startDate, endDate))
  );
  const preSessions = allSessions.filter(s => preIds.includes(s.id));
  const postSessions = allSessions.filter(s => postIds.includes(s.id));

  const handleStartDateInput = (val: string) => {
    setStartDate(val || null);
    if (val) {
      const { year, month } = ymdToYearMonth(val);
      setCalYear(year);
      setCalMonth(month);
    }
  };

  const handleEndDateInput = (val: string) => {
    setEndDate(val || null);
    if (val) {
      const { year, month } = ymdToYearMonth(val);
      setCalYear(year);
      setCalMonth(month);
    }
  };

  const handleTodayClick = () => {
    const t = today();
    setStartDate(t);
    setEndDate(t);
    const { year, month } = ymdToYearMonth(t);
    setCalYear(year);
    setCalMonth(month);
  };

  // Calendar grid
  const days = calDays(calYear, calMonth);

  const handleCalMouseDown = (date: string) => {
    setIsDraggingCal(true);
    setCalDragStart(date);
    setHoverDate(date);
  };

  const handleCalMouseEnter = (date: string) => {
    if (isDraggingCal) setHoverDate(date);
  };

  const handleCalMouseUp = useCallback(() => {
    if (isDraggingCal && calDragStart && hoverDate) {
      const [s, e] = [calDragStart, hoverDate].sort();
      setStartDate(s);
      setEndDate(e);
    }
    setIsDraggingCal(false);
    setCalDragStart(null);
  }, [isDraggingCal, calDragStart, hoverDate]);

  useEffect(() => {
    window.addEventListener('mouseup', handleCalMouseUp);
    return () => window.removeEventListener('mouseup', handleCalMouseUp);
  }, [handleCalMouseUp]);

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  };

  // Drag-drop session chips
  const onChipDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('sessionId', id);
  };

  const onDropZone = (zone: 'pre' | 'post') => (e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('sessionId');
    if (!id) return;
    if (zone === 'pre') {
      setPreIds(p => [...p.filter(x => x !== id), id]);
      setPostIds(p => p.filter(x => x !== id));
    } else {
      setPostIds(p => [...p.filter(x => x !== id), id]);
      setPreIds(p => p.filter(x => x !== id));
    }
    setDropOver(null);
  };

  const onUnassignDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('sessionId');
    setPreIds(p => p.filter(x => x !== id));
    setPostIds(p => p.filter(x => x !== id));
  };

  const removeFromPre = (id: string) => setPreIds(p => p.filter(x => x !== id));
  const removeFromPost = (id: string) => setPostIds(p => p.filter(x => x !== id));

  // Upload CSV into a slot
  const handleUpload = async (files: FileList | null, slot: 'pre' | 'post') => {
    if (!files || !files.length) return;
    const arr = Array.from(files).filter(f => f.name.endsWith('.csv'));
    if (!arr.length) return;
    slot === 'pre' ? setUploadingPre(true) : setUploadingPost(true);
    try {
      const res = await (api as any).uploadSkyTrakCSV(arr);
      // Refresh sessions list
      const sRes = await (api as any).getSkyTrakSessions();
      if (sRes?.success) {
        const newSessions: SkSession[] = sRes.sessions;
        setAllSessions(newSessions);
        // Auto-assign newly uploaded sessions to the correct slot
        const newIds = newSessions
          .filter(s => !allSessions.find(old => old.id === s.id))
          .map(s => s.id);
        if (slot === 'pre') setPreIds(p => [...p, ...newIds]);
        else setPostIds(p => [...p, ...newIds]);
      }
    } finally {
      slot === 'pre' ? setUploadingPre(false) : setUploadingPost(false);
    }
  };

  const handleBuild = async () => {
    if (!startDate || !endDate) return;
    if (!isPatientSession && !selectedPatient) return;
    setBuildError(null);
    setSaving(true);
    try {
      await (api as any).createNeuroTrainingSession({
        title,
        exercises,
        start_date: startDate,
        end_date: endDate,
        pre_session_ids: preIds,
        post_session_ids: postIds,
        ...(selectedPatient ? { patient_uuid: selectedPatient.patient_uuid } : {}),
        ...(selectedExerciseIds.length ? { hw_exercise_ids: selectedExerciseIds } : {}),
      });
      onCreated();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to create session. Please try again.';
      setBuildError(msg);
    } finally {
      setSaving(false);
    }
  };

  const sessionLabel = (s: SkSession) => {
    const parsed = parseActivityFilename(s.source_filename);
    return parsed ? `${parsed.label} · ${s.shot_count} shots` : `${s.session_date} · ${s.shot_count} shots`;
  };

  const canBuild = !!startDate && !!endDate && (preIds.length > 0 || postIds.length > 0)
    && (isPatientSession || !!selectedPatient);

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        {/* ── Header ── */}
        <ModalHead>
          <ModalTitle>New Neuro Training Session</ModalTitle>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </ModalHead>

        <ModalBody>
          {/* ── Left: form + date range + session pool ── */}
          <LeftPane>
            {/* Patient selector — clinician only */}
            {!isPatientSession && (
              <div style={{ position: 'relative' }}>
                <Label>Patient</Label>
                <div style={{ position: 'relative' }}>
                  <Input
                    ref={patientSearchRef}
                    placeholder={patientsLoading ? 'Loading patients…' : 'Search by name or UUID…'}
                    value={selectedPatient
                      ? `${selectedPatient.first_name} ${selectedPatient.last_name}`.trim() || selectedPatient.patient_uuid
                      : patientSearch}
                    onChange={e => { setSelectedPatient(null); setPatientSearch(e.target.value); setShowPatientDropdown(true); }}
                    onFocus={() => setShowPatientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowPatientDropdown(false), 150)}
                    readOnly={!!selectedPatient}
                    style={{ paddingRight: selectedPatient ? 32 : undefined, cursor: selectedPatient ? 'pointer' : undefined }}
                    onClick={() => { if (selectedPatient) { setSelectedPatient(null); setPatientSearch(''); setAllSessions([]); setPreIds([]); setPostIds([]); } }}
                  />
                  {selectedPatient && (
                    <span style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      fontSize: 13, color: '#6b7280', cursor: 'pointer', userSelect: 'none',
                    }} onMouseDown={() => { setSelectedPatient(null); setPatientSearch(''); setAllSessions([]); setPreIds([]); setPostIds([]); }}>✕</span>
                  )}
                </div>
                {showPatientDropdown && !selectedPatient && filteredPatients.length > 0 && (
                  <div style={{
                    position: 'absolute', zIndex: 100, left: 0, right: 0, top: '100%',
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
                  }}>
                    {filteredPatients.map(p => (
                      <div
                        key={p.patient_uuid}
                        onMouseDown={() => { setSelectedPatient(p); setPatientSearch(''); setShowPatientDropdown(false); }}
                        style={{
                          padding: '10px 14px', cursor: 'pointer', fontSize: 13,
                          borderBottom: '1px solid #f3f4f6',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {`${p.first_name} ${p.last_name}`.trim() || 'Unknown'}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 8 }}>
                          {p.patient_uuid.slice(0, 8)}…
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {showPatientDropdown && !selectedPatient && filteredPatients.length === 0 && patientSearch && (
                  <div style={{
                    position: 'absolute', zIndex: 100, left: 0, right: 0, top: '100%',
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                    padding: '10px 14px', fontSize: 13, color: '#6b7280',
                  }}>No patients found</div>
                )}
              </div>
            )}

            <div>
              <Label>Session Name (optional)</Label>
              <Input
                placeholder="e.g. Week 1 Golf Rehab"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </div>
            <div>
              <Label>Neuro Exercises</Label>
              <Textarea
                placeholder={`Describe your neuro training plan, e.g.:\n\n• Develop specific complex movements on one side, both sides or bilateral.\n• At home gaze stability exercise.\n• Neck care home program.\n• Objective golf marker assessment.`}
                value={exercises}
                onChange={e => setExercises(e.target.value)}
              />
            </div>

            {/* Assigned homework exercise selector */}
            {hwAssignments.length > 0 && (
              <div style={{ position: 'relative' }}>
                <Label>Assigned Exercises <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94A3B8' }}>(optional, select from homework)</span></Label>
                <div style={{ position: 'relative' }}>
                  <Input
                    placeholder="Search assigned exercises…"
                    value={exerciseSearch}
                    onChange={e => { setExerciseSearch(e.target.value); setShowExerciseDropdown(true); }}
                    onFocus={() => setShowExerciseDropdown(true)}
                    onBlur={() => setTimeout(() => setShowExerciseDropdown(false), 150)}
                  />
                  {showExerciseDropdown && (
                    <ExerciseDropdown>
                      {hwAssignments
                        .filter(a => !exerciseSearch || a.exercise.name.toLowerCase().includes(exerciseSearch.toLowerCase()))
                        .map(a => (
                          <ExerciseOption
                            key={a.exercise.id}
                            $selected={selectedExerciseIds.includes(a.exercise.id)}
                            onMouseDown={() => {
                              setSelectedExerciseIds(prev =>
                                prev.includes(a.exercise.id)
                                  ? prev.filter(id => id !== a.exercise.id)
                                  : [...prev, a.exercise.id]
                              );
                              setExerciseSearch('');
                            }}
                          >
                            <span style={{
                              width: 14, height: 14, borderRadius: 3, border: '1.5px solid #CBD5E1',
                              background: selectedExerciseIds.includes(a.exercise.id) ? '#0369A1' : '#fff',
                              flexShrink: 0, display: 'inline-block',
                            }} />
                            <span style={{ flex: 1 }}>{a.exercise.name}</span>
                            {a.exercise.category && (
                              <span style={{ fontSize: 10, color: '#94A3B8', background: '#F1F5F9', borderRadius: 4, padding: '1px 5px' }}>
                                {a.exercise.category}
                              </span>
                            )}
                          </ExerciseOption>
                        ))
                      }
                      {hwAssignments.filter(a => !exerciseSearch || a.exercise.name.toLowerCase().includes(exerciseSearch.toLowerCase())).length === 0 && (
                        <div style={{ padding: '10px 14px', fontSize: 12, color: '#94A3B8' }}>No matching exercises</div>
                      )}
                    </ExerciseDropdown>
                  )}
                </div>
                {selectedExerciseIds.length > 0 && (
                  <ExerciseChipRow>
                    {selectedExerciseIds.map(id => {
                      const a = hwAssignments.find(a => a.exercise.id === id);
                      if (!a) return null;
                      return (
                        <ExerciseChip key={id}>
                          {a.exercise.name}
                          <ExerciseChipX onClick={() => setSelectedExerciseIds(prev => prev.filter(x => x !== id))}>×</ExerciseChipX>
                        </ExerciseChip>
                      );
                    })}
                  </ExerciseChipRow>
                )}
              </div>
            )}

            {/* Date range inputs */}
            <div>
              <Label>Training Window</Label>
              <DateInputRow style={{ marginBottom: 0 }}>
                <DateInputWrap>
                  <DateLabel>From</DateLabel>
                  <DateInput
                    type="date"
                    value={startDate || ''}
                    onChange={e => handleStartDateInput(e.target.value)}
                  />
                </DateInputWrap>
                <DateInputWrap>
                  <DateLabel>To</DateLabel>
                  <DateInput
                    type="date"
                    value={endDate || ''}
                    onChange={e => handleEndDateInput(e.target.value)}
                  />
                </DateInputWrap>
                <TodayBtn onClick={handleTodayClick}>Today</TodayBtn>
              </DateInputRow>
              {startDate && endDate && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: '#0369A1', fontWeight: 600 }}>
                  {fmtYMD(startDate)}{startDate !== endDate ? ` → ${fmtYMD(endDate)}` : ''}
                </div>
              )}
            </div>

            {/* Available sessions pool */}
            <UnassignedRow
              onDragOver={e => e.preventDefault()}
              onDrop={onUnassignDrop}
              style={{ marginTop: 0 }}
            >
              <Label>
                {startDate && endDate ? 'Sessions in window' : 'Available Sessions'}
                {unassigned.length > 0 && (
                  <span style={{ marginLeft: 6, fontWeight: 400, color: '#0369A1', textTransform: 'none', letterSpacing: 0 }}>
                    — drag to Pre or Post →
                  </span>
                )}
              </Label>
              {unassigned.length === 0 ? (
                <div style={{ fontSize: 12, color: '#94A3B8', padding: '10px 0' }}>
                  {sessionsLoading
                    ? 'Loading sessions…'
                    : allSessions.length === 0
                      ? 'No SkyTrak sessions yet. Upload a CSV in Golf Marker first.'
                      : 'No sessions in this date range. Adjust the window or upload a CSV.'}
                </div>
              ) : (
                unassigned.map(s => (
                  <SessionChip key={s.id} draggable onDragStart={e => onChipDragStart(e, s.id)}>
                    <ChipDot $color="#F59E0B" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{sessionLabel(s)}</div>
                      <div style={{ fontSize: 10, color: '#94A3B8' }}>{s.session_date}</div>
                    </div>
                  </SessionChip>
                ))
              )}
            </UnassignedRow>
          </LeftPane>

          {/* ── Right: calendar + pre/post zones ── */}
          <RightPane>
            <RightTop>
              {/* Calendar */}
              <CalNav>
                <CalNavBtn onClick={prevMonth}>‹</CalNavBtn>
                <CalMonth>{MONTH_NAMES[calMonth]} {calYear}</CalMonth>
                <CalNavBtn onClick={nextMonth}>›</CalNavBtn>
              </CalNav>
              <CalGrid>
                {DOW.map(d => <CalDOW key={d}>{d}</CalDOW>)}
                {days.map((date, i) => {
                  if (!date) return <div key={`empty-${i}`} style={{ height: 32 }} />;
                  const isEdge = date === tempStart || date === tempEnd;
                  const ranged = inRange(date, tempStart, tempEnd);
                  const hasSess = !!sessionsByDate[date]?.length;
                  const isToday = date === todayStr;
                  return (
                    <CalDay
                      key={date}
                      $today={isToday}
                      $inRange={ranged && !isEdge}
                      $isEdge={isEdge}
                      $hasSession={hasSess}
                      onMouseDown={() => handleCalMouseDown(date)}
                      onMouseEnter={() => handleCalMouseEnter(date)}
                    >
                      {parseInt(date.split('-')[2])}
                      {hasSess && <SessionDot />}
                    </CalDay>
                  );
                })}
              </CalGrid>
            </RightTop>

            <RightBottom>
              {/* Pre / Post drop zones */}
              <AssignRow>
                {/* Pre Training */}
                <div>
                  {isPatientSession && (
                    <input ref={preFileRef} type="file" accept=".csv" multiple style={{ display: 'none' }}
                      onChange={e => handleUpload(e.target.files, 'pre')} />
                  )}
                  <DropZone
                    $over={dropOver === 'pre'}
                    onDragOver={e => { e.preventDefault(); setDropOver('pre'); }}
                    onDragLeave={() => setDropOver(null)}
                    onDrop={onDropZone('pre')}
                  >
                    <DropZoneLabel $color="#059669">▲ Pre Training</DropZoneLabel>
                    {preSessions.length === 0
                      ? <DropHint>Drag sessions here</DropHint>
                      : preSessions.map(s => (
                        <SessionChip key={s.id} draggable onDragStart={e => onChipDragStart(e, s.id)}>
                          <ChipDot $color="#059669" />
                          <span style={{ flex: 1, fontSize: 11.5 }}>{sessionLabel(s)}</span>
                          <span style={{ cursor: 'pointer', color: '#94A3B8', fontSize: 14 }}
                            onClick={() => removeFromPre(s.id)}>×</span>
                        </SessionChip>
                      ))
                    }
                    {isPatientSession && (
                      <UploadInZone onClick={() => preFileRef.current?.click()}>
                        {uploadingPre ? '⏳ Uploading…' : '+ Upload CSV'}
                      </UploadInZone>
                    )}
                  </DropZone>
                </div>

                {/* Post Training */}
                <div>
                  {isPatientSession && (
                    <input ref={postFileRef} type="file" accept=".csv" multiple style={{ display: 'none' }}
                      onChange={e => handleUpload(e.target.files, 'post')} />
                  )}
                  <DropZone
                    $over={dropOver === 'post'}
                    onDragOver={e => { e.preventDefault(); setDropOver('post'); }}
                    onDragLeave={() => setDropOver(null)}
                    onDrop={onDropZone('post')}
                  >
                    <DropZoneLabel $color="#0369A1">▼ Post Training</DropZoneLabel>
                    {postSessions.length === 0
                      ? <DropHint>Drag sessions here</DropHint>
                      : postSessions.map(s => (
                        <SessionChip key={s.id} draggable onDragStart={e => onChipDragStart(e, s.id)}>
                          <ChipDot $color="#0369A1" />
                          <span style={{ flex: 1, fontSize: 11.5 }}>{sessionLabel(s)}</span>
                          <span style={{ cursor: 'pointer', color: '#94A3B8', fontSize: 14 }}
                            onClick={() => removeFromPost(s.id)}>×</span>
                        </SessionChip>
                      ))
                    }
                    {isPatientSession && (
                      <UploadInZone onClick={() => postFileRef.current?.click()}>
                        {uploadingPost ? '⏳ Uploading…' : '+ Upload CSV'}
                      </UploadInZone>
                    )}
                  </DropZone>
                </div>
              </AssignRow>
            </RightBottom>
          </RightPane>
        </ModalBody>

        {/* ── Footer ── */}
        <ModalFoot>
          {buildError && (
            <span style={{ color: '#EF4444', fontSize: 13, flex: 1 }}>{buildError}</span>
          )}
          <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
          <BtnPrimary $disabled={!canBuild || saving} onClick={canBuild && !saving ? handleBuild : undefined}>
            {saving ? 'Saving…' : 'Build Training Session'}
          </BtnPrimary>
        </ModalFoot>
      </Modal>
    </Overlay>
  );
};

export default CreateTrainingModal;
